import UndoIcon from "@mui/icons-material/Undo";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    Grid,
    TextField,
} from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { companyNationalIdInputProps, yupCompanyNationalIdWhenLegal } from "../../../../../infrastructure/utils/companyNationalIdRules";

import DefaultApiService from "../../../../../infrastructure/api/default";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DropdownPicker from "../../../../components/shared/dropdown";
import FileUploader from "../../../../components/shared/uploader/FileUploader";

const validationSchema = Yup.object().shape({
  mobile: Yup.string().required("لطفا این فیلد را پر کنید."),
  telephon: Yup.string().required("لطفا این فیلد را پر کنید."),

  agreementKind: Yup.string().required("لطفا این فیلد را پر کنید."),
  companyNationalCode: yupCompanyNationalIdWhenLegal(),
  companyRegisterationCode: Yup.string().when("agreementKind", {
    is: (val) => val === "Legal",
    then: (s) => s.required("لطفا این فیلد را پر کنید."),
    otherwise: (s) => s.nullable(),
  }),
});

const CustomerProfileModifyPage = () => {
  const data = useLoaderData("root");
  let navigate = useNavigate();
  const { id: requestId } = useParams();

  const [loading, setLoading] = useState(false);

  const onNavigateToListPage = () => {
    navigate("/SLN/Customer/CustomerProfile");
  };

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);
    const sendData = { ...data.substation, ...formValue };

    try {
      console.log(sendData);

      const res = await DefaultApiService.modifyApi(
        "SLN/Customer/CustomerProfile",
        sendData
      );

      console.log(res);
      if (res.isSuccess) {
        toast.success(res.message);
        onNavigateToListPage();
      } else {
        console.log(res);
        toast.error(res.message);
      }

      setLoading(false);
    } catch (error) {
      console.log(error);
      const errorList = error?.response?.data;
      if (typeof errorList === "object") {
        for (const key in errorList) {
          if (Object.hasOwnProperty.call(errorList, key)) {
            const list = errorList[key];
            toast.error(list[0]);
          }
        }
      } else toast.error("خطایی رخ داده است.");

      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              requestId !== "create"
                ? `ویرایش درخواست نیروگاه ${data?.customerInfo?.fullName}`
                : "ثبت درخواست نیروگاه"
            }
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                }}
              >
                <Button
                  variant="outlined"
                  color="gray"
                  startIcon={<UndoIcon />}
                  onClick={onNavigateToListPage}
                >
                  بازگشت
                </Button>
              </Box>
            }
          />

          <CardContent>
            <Formik
              initialValues={data.customerInfo}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const onMakeFormikVariable = (name) => ({
                  name: name,
                  fullWidth: true,
                  value: values[name],
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                });

                return (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6} lg={3}>
                        <DropdownPicker
                          label="نوع درخواست"
                          onChange={(event) => {
                            setFieldValue("agreementKind", event.target.value);
                          }}
                          list={data.agreementKind}
                          filterable={false}
                          {...onMakeFormikVariable("agreementKind")}
                        />
                      </Grid>

                      {values.agreementKind === "Legal" ? (
                        <>
                          <Grid item xs={12} md={6} lg={3}>
                            <TextField
                              label="شناسه ملی شرکت"
                              onChange={wrapFormikHandleChange(handleChange)}
                              inputProps={companyNationalIdInputProps}
                              {...onMakeFormikVariable("companyNationalCode")}
                            />
                          </Grid>

                          <Grid item xs={12} md={6} lg={3}>
                            <TextField
                              label="شماره ثبت شرکت"
                              onChange={wrapFormikHandleChange(handleChange)}
                              {...onMakeFormikVariable(
                                "companyRegisterationCode"
                              )}
                            />
                          </Grid>
                        </>
                      ) : null}

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="نام متقاضی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("firstName")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="نام خانوادگی متقاضی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("lastName")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="همراه متقاضی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("mobile")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="تلفن متقاضی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("telephon")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="ایمیل متقاضی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("email")}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FileUploader
                          moduleName={"SLN/CustomerData"}
                          maxFiles={1}
                          label="تصویر کارت ملی "
                          onChange={(list) => {
                            if (list.length > 0) {
                              setFieldValue("nationalCardUrl", list[0]);
                            } else {
                              setFieldValue("nationalCardUrl", "");
                            }
                          }}
                          baseUrl={data.baseUrl}
                          placeholder={"تصویر مورد نظر را انتخاب کنید"}
                          accept="image/*"
                          {...onMakeFormikVariable("nationalCardUrl")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FileUploader
                          moduleName={"SLN/CustomerData"}
                          maxFiles={1}
                          label="تصویر شناسنامه"
                          onChange={(list) => {
                            if (list.length > 0) {
                              setFieldValue("birthCertificateUrl", list[0]);
                            } else {
                              setFieldValue("birthCertificateUrl", "");
                            }
                          }}
                          baseUrl={data.baseUrl}
                          placeholder={"تصویر مورد نظر را انتخاب کنید"}
                          accept="image/*"
                          {...onMakeFormikVariable("birthCertificateUrl")}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={12}
                        container
                        justifyContent={"flex-end"}
                      >
                        <Button
                          disabled={isSubmitting}
                          variant="outlined"
                          size="large"
                          color="gray"
                          sx={{ mx: 1 }}
                          onClick={onNavigateToListPage}
                        >
                          انصراف
                        </Button>

                        <Button
                          disabled={isSubmitting}
                          variant="contained"
                          type="submit"
                          size="large"
                        >
                          {loading && (
                            <CircularProgress size={16} color="inherit" />
                          )}
                          {requestId !== "create" ? "ثبت" : "ایجاد"}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CustomerProfileModifyPage;
